import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, getThemeColors } from '../../../../lib/theme';
import { v1Notices } from '../../../../lib/v1';
import { familyAccountNotices } from '../../../../lib/familyAccountData';
import type { NoticeRow, PersonOption } from './types';

export interface NoticeFormProps {
  dark: boolean;
  userId: number;
  initial?: NoticeRow | null;
  onSaved: (notice: NoticeRow) => void;
  onCancel: () => void;
  /**
   * Batch-13 Paket E — Familienkonto-Zentrierung: wenn gesetzt, speichert das
   * Formular über die Konto-Endpoints statt personenbezogen über v1.
   */
  accountId?: number | null;
  /** Mitglieder des Kontos für die optionale Personen-Auswahl. */
  members?: PersonOption[];
}

/**
 * localStorage-Key für den Notiz-Entwurf. Neue Notizen werden pro PB
 * zwischengespeichert, damit der eingegebene Text einen Reload überlebt.
 */
export function noticeDraftKey(userId: number, noticeId?: number | null): string {
  return noticeId ? `sh24:notice-draft:edit:${noticeId}` : `sh24:notice-draft:new:${userId}`;
}

export function readNoticeDraft(userId: number, noticeId?: number | null): string {
  try { return localStorage.getItem(noticeDraftKey(userId, noticeId)) ?? ''; } catch { return ''; }
}

export function NoticeForm({ dark, userId, initial, onSaved, onCancel, accountId, members }: NoticeFormProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const serverValue = initial?.comment ?? initial?.nachricht ?? initial?.text ?? '';
  // Entwurf aus localStorage bevorzugen (überlebt Reload), sonst Server-Wert.
  const [comment, setComment] = useState(() => readNoticeDraft(userId, initial?.id) || serverValue);
  // Batch-13 Paket E — Personen-Zuordnung: im Konto-Modus per Default KEINE
  // Person ausgewählt (Konto-weite Notiz), außer beim Bearbeiten mit user_id.
  const [personId, setPersonId] = useState<number | ''>(
    accountId != null ? (initial?.user_id ?? '') : '',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { textareaRef.current?.focus(); }, []);

  // Sofort speichern: jede Eingabe wird als Entwurf persistiert, damit der Text
  // beim Neuladen der Seite nicht verloren geht. Leerer Text → Entwurf löschen.
  useEffect(() => {
    try {
      const key = noticeDraftKey(userId, initial?.id);
      if (comment.trim() && comment !== serverValue) localStorage.setItem(key, comment);
      else localStorage.removeItem(key);
    } catch { /* localStorage nicht verfügbar — ignorieren */ }
  }, [comment, userId, initial?.id, serverValue]);

  const clearDraft = useCallback(() => {
    try { localStorage.removeItem(noticeDraftKey(userId, initial?.id)); } catch { /* ignore */ }
  }, [userId, initial?.id]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) { setError(tr('pflege.detail.dashboard.notice_required')); return; }
    setSaving(true);
    setError(null);
    try {
      let saved: NoticeRow;
      if (accountId != null) {
        const userIdForSave = personId === '' ? null : personId;
        saved = initial?.id
          ? await familyAccountNotices.update(initial.id, { comment: comment.trim(), user_id: userIdForSave }) as NoticeRow
          : await familyAccountNotices.create(accountId, { comment: comment.trim(), user_id: userIdForSave }) as NoticeRow;
      } else if (initial?.id) {
        saved = await v1Notices.update(initial.id, { comment: comment.trim() }) as NoticeRow;
      } else {
        saved = await v1Notices.create({ user_id: userId, comment: comment.trim() }) as NoticeRow;
      }
      clearDraft();
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : tr('pflege.detail.dashboard.save_error'));
    } finally {
      setSaving(false);
    }
  }, [comment, userId, personId, accountId, initial, tr, onSaved, clearDraft]);

  const handleCancel = useCallback(() => { clearDraft(); onCancel(); }, [clearDraft, onCancel]);

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 16px', borderBottom: `1px solid ${t.border}`, background: t.hover }}>
      <textarea
        ref={textareaRef}
        rows={3}
        placeholder={tr('pflege.detail.dashboard.notice_placeholder')}
        value={comment}
        onChange={e => setComment(e.target.value)}
        style={{ fontSize: 13, padding: '7px 10px', border: `1px solid ${t.border}`, borderRadius: 7, background: t.inputBg, color: t.text, outline: 'none', resize: 'vertical', fontFamily: 'Outfit,sans-serif', width: '100%', boxSizing: 'border-box' }}
      />
      {/* Batch-13 Paket E — Personen-Zuordnung (nur im Konto-Modus): optional,
          Default "Konto" (kein Personenbezug). */}
      {accountId != null && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: t.muted }}>
          {tr('pflege.tasks.person_label')}
          <select
            aria-label={tr('pflege.tasks.person_label')}
            value={personId}
            onChange={e => setPersonId(e.target.value === '' ? '' : Number(e.target.value))}
            style={{ fontSize: 13, padding: '7px 10px', border: `1px solid ${t.border}`, borderRadius: 7, background: t.inputBg, color: t.text, outline: 'none', fontFamily: 'Outfit,sans-serif', width: '100%', boxSizing: 'border-box' }}
          >
            <option value="">{tr('pflege.tasks.person_none')}</option>
            {(members ?? []).map(m => (
              <option key={m.user_id} value={m.user_id}>{m.name}</option>
            ))}
          </select>
        </label>
      )}
      {error && <div style={{ fontSize: 11.5, color: '#b42318' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" disabled={saving} style={{ flex: 1, fontSize: 13, fontWeight: 600, background: BRAND, color: '#fff', border: 'none', borderRadius: 7, padding: '7px 0', cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? '…' : (initial?.id ? tr('common.action.save') : tr('pflege.detail.dashboard.hinzufuegen'))}
        </button>
        <button type="button" onClick={handleCancel} style={{ fontSize: 13, padding: '7px 14px', background: 'none', border: `1px solid ${t.border}`, borderRadius: 7, color: t.muted, cursor: 'pointer' }}>
          {tr('common.action.cancel')}
        </button>
      </div>
    </form>
  );
}
